import { WEEK_DAYS, type DayId } from './days'
import type { CycleProfile } from './cycle'
import { isLateLuteal } from './cycle'
import type { GroceryItem, MealBlock, StorageProtocol } from './protocolTypes'

export type PrepStance = 'DAILY' | 'BATCH'

export function defaultStorageProtocol(dishName: string): StorageProtocol {
  const lower = dishName.toLowerCase()
  if (lower.includes('rice')) {
    return {
      title: 'Rapid-Cool Rice Protocol',
      rules: [
        'Spread cooked rice on a flat tray within 20 minutes of cooking.',
        'Cool to below 5°C within 1 hour before refrigerating.',
        'Consume within 24 hours or freeze in portioned containers.',
      ],
      hazard_level: 'high',
    }
  }

  return {
    title: 'Compartmentalization Protocol',
    rules: [
      'Store proteins, carbs, and sauces in separate airtight containers.',
      'Reheat proteins to 74°C core temp; add fresh greens after reheating.',
      'Label containers with prep date and consume within 3 days.',
    ],
    hazard_level: 'medium',
  }
}

export function applyLutealMacroAdjustments(meals: MealBlock[], cycle: CycleProfile | null): MealBlock[] {
  if (!cycle || !isLateLuteal(cycle)) return meals

  return meals.map((meal) => {
    const lutealFriendly = /cacao|salmon|sweet potato|oats|magnesium/i.test(meal.dish_name)
    return {
      ...meal,
      calories_kcal: meal.calories_kcal + Math.round(150 / Math.max(meals.length, 1)),
      rationale_why: lutealFriendly
        ? `${meal.rationale_why} Late-luteal phase: magnesium-rich, high-satiety selection to stabilize cravings.`
        : `${meal.rationale_why} +150 kcal metabolic safety buffer for late-luteal phase.`,
    }
  })
}

export function getMealsForPrepWindow(meals: MealBlock[], startDayId: DayId, windowDays: number): MealBlock[] {
  const startIdx = WEEK_DAYS.findIndex((d) => d.id === startDayId)
  if (startIdx === -1) return meals

  const dayIds = Array.from({ length: windowDays }, (_, i) => WEEK_DAYS[(startIdx + i) % 7].id)
  return meals.filter((m) => dayIds.includes(m.day_id))
}

export function scaleGroceryForBatch(items: GroceryItem[], windowDays: number): GroceryItem[] {
  if (windowDays <= 1) return items
  return items.map((item) => ({
    ...item,
    quantity_mass: `${item.quantity_mass} × ${windowDays}d batch`,
    approx_cost: Math.round(item.approx_cost * windowDays * 100) / 100,
  }))
}
